import React from 'react';
import { useServerContext } from '$/hooks/useServerDateNow';
import { plural, t } from '@lingui/macro';
import { langDirClassname } from '$/shared/i18n';

export function getDiffTimeFromNow(
  date: Date,
  now?: Date
): {
  diff: number;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
} {
  const diffTime = new Date(date).getTime() - (now?.getTime() ?? Date.now());
  const days = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diffTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diffTime % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diffTime % (1000 * 60)) / 1000);
  return {
    diff: diffTime,
    days,
    hours,
    minutes,
    seconds,
  };
}

export const TimeLeftToDate: React.FC<{ date: Date; lang: string }> = ({ date, lang }) => {
  const { serverDateNow } = useServerContext();
  const {
    diff,
    days: daysFromNow,
    hours: hoursFromNow,
    minutes: minutesFromNow,
    seconds: secondsFromNow,
  } = getDiffTimeFromNow(date, serverDateNow());

  if (diff <= 0) {
    return <b>{t`NOW`}</b>;
  }

  const dir = langDirClassname(lang);

  return (
    <span dir={dir}>
      {daysFromNow > 0 && (
        <span dir='ltr'>
          <b className='number'>{daysFromNow}</b>
          <span className='unit' dir={dir}>
            {plural(daysFromNow, {
              one: 'day',
              other: 'days',
            }).toUpperCase()}
          </span>
        </span>
      )}

      {(daysFromNow > 0 || hoursFromNow > 0) && (
        <span dir='ltr'>
          <b className='number'>{`${hoursFromNow}`.padStart(2, '0')}</b>
          <span className='unit' dir={dir}>
            {plural(hoursFromNow, {
              one: 'hour',
              other: 'hours',
            }).toUpperCase()}
          </span>
        </span>
      )}
      <span dir='ltr'>
        <b className='number'>{`${minutesFromNow}`.padStart(2, '0')}</b>
        <span className='unit' dir={dir}>
          {plural(minutesFromNow, {
            one: 'minute',
            other: 'minutes',
          }).toUpperCase()}
        </span>
      </span>

      <span dir='ltr'>
        <b className='number'>{`${secondsFromNow}`.padStart(2, '0')}</b>
        <span className='unit' dir={dir}>
          {plural(secondsFromNow, {
            one: 'second',
            other: 'seconds',
          }).toUpperCase()}
        </span>
      </span>
    </span>
  );
};
